import { ItemTypeConsts } from 'client/util/Constants';
import { onboardingFeatures } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { simulations } from 'client/util/Constants/SimulationConstants';

const widgetTypes = {
  agentInstalls: 'agentInstalls',
  blog: 'blog',
  community: 'community',
  consoleStats: 'consoleStats',
  deviceNotifications: 'deviceNotifications',
  deviceNotificationInvestigation: 'deviceNotificationInvestigation',
  diskEncryption: 'diskEncryption',
  expiredPasswords: 'expiredPasswords',
  fundamentals: 'fundamentals',
  guidedSetup: 'guidedSetup',
  jcUniversity: 'jcUniversity',
  learningCenter: 'learningCenter',
  manualGroupMembership: 'manualGroupMembership',
  mdmCertificateExpirations: 'mdmCertificateExpirations',
  newOSReleases: 'newOSReleases',
  newUsers: 'newUsers',
  officeHours: 'officeHours',
  reportsReadyForDownload: 'reportsReadyForDownload',
  ScheduledUserSuspensions: 'ScheduledUserSuspensions',
  ScheduledUserActivations: 'ScheduledUserActivations',
  ScheduledUserAccess: 'ScheduledUserAccess',
  tutorialResources: 'tutorialResources',
  unencryptedDevices: 'unencryptedDevices',
  upcomingPasswordExpiration: 'upcomingPasswordExpiration',
  userGroupSuggestions: 'userGroupSuggestions',
  userLockouts: 'userLockouts',
  userNotifications: 'userNotifications',
};

const apiPayloadConst = {
  maxFetchLimit: 100,
};

const deviceMetrics = {
  inactiveMetric: 'inactiveMetric',
  uptimeMetric: 'uptimeMetric',
};

const widgetNames = {
  [widgetTypes.agentInstalls]: 'MacOS Issues',
  [widgetTypes.blog]: 'Blog',
  [widgetTypes.consoleStats]: 'Organizational Stats',
  [widgetTypes.community]: 'Community',
  [widgetTypes.deviceNotifications]: 'Device Notifications',
  [widgetTypes.diskEncryption]: 'Disk Encryption',
  [widgetTypes.expiredPasswords]: 'Expired Passwords',
  [widgetTypes.fundamentals]: 'Fundamentals',
  [widgetTypes.manualGroupMembership]: 'Group Membership Updates',
  [widgetTypes.guidedSetup]: 'Guided Setup',
  [widgetTypes.jcUniversity]: 'JumpCloud University',
  [widgetTypes.learningCenter]: 'Learning Center',
  [widgetTypes.mdmCertificateExpirations]: 'MDM Upcoming Certificate Expirations',
  [widgetTypes.newOSReleases]: 'Recent OS Releases (Past 14 Days)',
  [widgetTypes.reportsReadyForDownload]: 'Reports Ready For Download (Past 7 Days)',
  [widgetTypes.newUsers]: 'New Users (Past 7 Days)',
  [widgetTypes.officeHours]: 'JumpCloud Office Hours',
  [widgetTypes.tutorialResources]: 'Tutorial Resources',
  [widgetTypes.userGroupSuggestions]: 'User Group Suggestions',
  [widgetTypes.unencryptedDevices]: 'Unencrypted Devices',
  [widgetTypes.upcomingPasswordExpiration]: 'Upcoming Password Expirations',
  [widgetTypes.userLockouts]: 'User Lockouts',
  [widgetTypes.userNotifications]: 'User Notifications',
  [widgetTypes.ScheduledUserSuspensions]: 'Scheduled User Suspensions',
  [widgetTypes.ScheduledUserActivations]: 'Scheduled User Activations',
  [widgetTypes.ScheduledUserAccess]: 'Scheduled User State Changes',
};

const widgetDisplayName = {
  [widgetTypes.deviceInactive]: 'Devices Inactive Greater Than 7 Days',
  [widgetTypes.newUsers]: 'New Users',
  [widgetTypes.userLockouts]: 'Users Locked Out',
  [widgetTypes.expiredPasswords]: 'Users Password Expired',
  [widgetTypes.manualGroupMembership]: 'Group Membership Updates',
  [widgetTypes.upcomingPasswordExpiration]: 'Users Upcoming Password Expiration',
  [widgetTypes.ScheduledUserSuspensions]: 'Scheduled User Suspensions',
  [widgetTypes.ScheduledUserActivations]: 'Scheduled User Activations',
  [widgetTypes.ScheduledUserAccess]: 'Scheduled User State Changes',
  [widgetTypes.userGroupSuggestions]: 'User Group Suggestions',
};

const widgetIcons = {
  [widgetTypes.newUsers]: 'info',
  [widgetTypes.reportsReadyForDownload]: 'info',
  [widgetTypes.userLockouts]: 'error',
  [widgetTypes.expiredPasswords]: 'error',
  [widgetTypes.ScheduledUserSuspensions]: 'info',
  [widgetTypes.ScheduledUserActivations]: 'info',
  [widgetTypes.upcomingPasswordExpiration]: 'error',
  [widgetTypes.userGroupSuggestions]: 'info',
};

const widgetTableColumns = {
  [widgetTypes.newUsers]: [
    {
      dataFieldName: 'state', label: 'User State', width: '1.3fr', sortable: true,
    },
    {
      dataFieldName: 'linklastName', label: 'User Name', width: '1fr', sortable: true,
    },
    {
      dataFieldName: 'email', label: 'Email', width: '2fr', sortable: true,
    },
    {
      dataFieldName: 'passwordStatus', label: 'Password Status', width: '1.5fr', sortable: true,
    },
    {
      dataFieldName: 'mfaStatus', label: 'MFA Status', width: '2fr', sortable: true,
    },
    { dataFieldName: 'actions', label: 'Actions', width: '3fr' },
  ],
  [widgetTypes.userLockouts]: [
    {
      dataFieldName: 'state', label: 'User State', width: '1.2fr', sortable: true,
    },
    {
      dataFieldName: 'lastName', label: 'User Name', width: '2.5fr', sortable: true,
    },
    {
      dataFieldName: 'timeStamp', label: 'Timestamp', width: '1.5fr', sortable: true,
    },
    { dataFieldName: 'actions', label: 'Actions', width: '3fr' },
  ],
  [widgetTypes.expiredPasswords]: [
    {
      dataFieldName: 'state', label: 'User State', width: '1.2fr', sortable: true,
    },
    {
      dataFieldName: 'linklastName', label: 'User Name', width: '1fr', sortable: true,
    },
    {
      dataFieldName: 'email', label: 'Email', width: '2fr', sortable: true,
    },
    {
      dataFieldName: 'passwordExpiredTimeStamp', label: 'Timestamp', width: '1fr', sortable: true,
    },
    {
      dataFieldName: 'actions', label: 'Actions', width: '1fr',
    },
  ],
  [widgetTypes.reportsReadyForDownload]: [
    {
      dataFieldName: 'type', label: 'Report Name', width: '1.2fr', sortable: true,
    },
    {
      dataFieldName: 'requester_email', label: 'Created By', width: '1.5fr', sortable: true,
    },
    {
      dataFieldName: 'created_at', label: 'Created Date', width: '1.5fr', sortable: true,
    },
    {
      dataFieldName: 'expiration', label: 'Expiration Date', width: '1.5fr', sortable: true,
    },
    {
      dataFieldName: 'actions', label: 'Actions', width: '1.5fr',
    },
  ],
  [widgetTypes.ScheduledUserSuspensions]: [
    { dataFieldName: 'state', label: 'User State', sortable: true },
    { dataFieldName: 'lastName', label: 'User Name', sortable: true },
    { dataFieldName: 'email', label: 'Email', sortable: true },
    { dataFieldName: 'scheduleDate', label: 'Suspension Date', sortable: true },
  ],
  [widgetTypes.ScheduledUserActivations]: [
    { dataFieldName: 'state', label: 'User State', sortable: true },
    { dataFieldName: 'lastName', label: 'User Name', sortable: true },
    { dataFieldName: 'email', label: 'Email', sortable: true },
    { dataFieldName: 'scheduleDate', label: 'Activation Date', sortable: true },
  ],
  [widgetTypes.upcomingPasswordExpiration]: [
    {
      dataFieldName: 'state', label: 'User State', width: '1.2fr', sortable: true,
    },
    {
      dataFieldName: 'linklastName', label: 'User Name', width: '1fr', sortable: true,
    },
    {
      dataFieldName: 'email', label: 'Email', width: '2fr', sortable: true,
    },
    {
      dataFieldName: 'passwordExpiredTimeStamp', label: 'Timestamp', width: '1fr', sortable: true,
    },
    {
      dataFieldName: 'actions', label: 'Actions', width: '3fr',
    },
  ],
  [widgetTypes.userGroupSuggestions]: [
    {
      dataFieldName: 'linklastName', label: 'User Name', width: '1fr', sortable: true,
    },
    {
      dataFieldName: 'groupname', label: 'Group Name', width: '1.2fr', sortable: true,
    },
    {
      dataFieldName: 'email', label: 'Email', width: '2fr', sortable: true,
    },
    {
      dataFieldName: 'suggestion', label: 'Suggestion', width: '2fr', sortable: true,
    },
  ],
  [widgetTypes.unencryptedDevices]: [
    { dataFieldName: 'status', label: 'Status' },
    { dataFieldName: 'deviceName', label: 'Device Name' },
    { dataFieldName: 'os', label: 'Operating System' },
  ],
  [widgetTypes.deviceNotificationInvestigation]: [
    {
      dataFieldName: 'active', label: 'Status', sortable: true, width: '.5fr',
    },
    {
      dataFieldName: 'displayName', label: 'Device Name', sortable: true, width: '1fr',
    },
    {
      dataFieldName: 'os', label: 'OS', sortable: true, width: '1fr',
    },
    {
      dataFieldName: 'lastContact', label: 'Last Contact', sortable: true, width: '1fr',
    },
  ],
  [deviceMetrics.uptimeMetric]: [
    {
      dataFieldName: 'displayName', label: 'Device Name', sortable: true, width: '1fr',
    },
    {
      dataFieldName: 'status', label: 'Status', sortable: true, width: '1fr',
    },
    {
      dataFieldName: 'os', label: 'OS', sortable: true, width: '1fr',
    },
    {
      dataFieldName: 'upTime', label: 'Uptime', sortable: true, width: '1fr',
    },
  ],
  [widgetTypes.manualGroupMembership]: [
    {
      dataFieldName: 'group',
      label: 'Group',
      width: '1.0fr',
      sortable: true,
    },
    {
      dataFieldName: 'user_name',
      label: 'User Name',
      width: '0.7fr',
      sortable: true,
    },
    {
      dataFieldName: 'user_state',
      label: 'User State',
      width: '0.7fr',
      sortable: true,
    },
    {
      dataFieldName: 'email',
      label: 'Email',
      width: '2fr',
      sortable: true,
    },
    {
      dataFieldName: 'timestamp',
      label: 'Timestamp',
      width: '1.3fr',
      sortable: true,
    },
    {
      dataFieldName: 'user_group_action_from',
      label: 'User Group Name Added/Removed',
      width: '1.0fr',
    },
    {
      dataFieldName: 'action',
      label: 'Actions',
      width: '1.0fr',
    },
  ],
};

const widgetSortOption = {
  [widgetTypes.newUsers]: null,
  [widgetTypes.userLockouts]: '-account_locked_date',
  [widgetTypes.expiredPasswords]: '-password_expiration_date',
  [widgetTypes.upcomingPasswordExpiration]: null,
  [widgetTypes.unencryptedDevices]: null,
};

const widgetActions = {
  [widgetTypes.expiredPasswords]: [
    {
      name: 'commands',
      iconName: 'Commands',
      icon: 'commands',
      click: 'sendSelectedUsersEmailContinue',
      toolTipText: 'Email',
    },
  ],
  [widgetTypes.reportsReadyForDownload]: [
    {
      name: 'exportJson',
      iconName: 'fileUpload',
      icon: 'fileUpload',
      click: 'handleExportJson',
      toolTipText: 'Export JSON',
    },
    {
      name: 'exportCsv',
      iconName: 'fileUpload',
      icon: 'fileUpload',
      click: 'handleExportCsv',
      toolTipText: 'Export CSV',
    },
  ],
  [widgetTypes.newUsers]: [
    {
      name: 'refresh',
      iconName: 'Refresh',
      icon: 'refresh',
      click: 'handlePasswordChange',
      toolTipText: 'Reset',
    },
    {
      name: 'commands',
      iconName: 'Commands',
      icon: 'commands',
      click: 'sendSelectedUsersEmailContinue',
      toolTipText: 'Email',
    },
  ],
  [widgetTypes.userLockouts]: [
    {
      name: 'unlock',
      iconName: 'Unlock',
      icon: 'unlocked',
      click: 'handleUnlock',
      toolTipText: 'Unlock',
    },
    {
      name: 'suspend',
      iconName: 'Suspend',
      icon: 'pauseCircle',
      click: 'handleSuspend',
      toolTipText: 'Suspend',
    },
    {
      name: 'refresh',
      iconName: 'Refresh',
      icon: 'refresh',
      click: 'handlePasswordChange',
      toolTipText: 'Reset',
    },
  ],
  [widgetTypes.upcomingPasswordExpiration]: [
    {
      name: 'refresh',
      iconName: 'Refresh',
      icon: 'refresh',
      click: 'handlePasswordChange',
      toolTipText: 'Reset',
    },
    {
      name: 'commands',
      iconName: 'Commands',
      icon: 'commands',
      click: 'sendSelectedUsersEmailContinue',
      toolTipText: 'Email',
    },
  ],
};

const itemTypes = {
  device: 'Device',
  user: 'User',
};

const widgetItemTypes = {
  [widgetTypes.agentInstalls]: itemTypes.device,
  [widgetTypes.deviceNotifications]: itemTypes.device,
  [widgetTypes.diskEncryption]: itemTypes.device,
  [widgetTypes.expiredPasswords]: itemTypes.user,
  [widgetTypes.upcomingPasswordExpiration]: itemTypes.user,
  [widgetTypes.userLockouts]: itemTypes.user,
};

const recentOsReleasesNames = {
  macOS: 'Mac',
  windows: 'Windows',
  ubuntu: 'Ubuntu',
};

const userNotificationMetrics = {
  usersWithoutPassword: 'usersWithoutPassword',
  usersWithSambaAccess: 'usersWithSambaAccess',
  userWithAdminSudoPasswordlessAccess: 'userWithAdminSudoPasswordlessAccess',
  usersWithAdminSudoAccess: 'usersWithAdminSudoAccess',
  usersWithoutDevices: 'usersWithoutDevices',
};

const userNotificationMetricsNames = {
  [userNotificationMetrics.usersWithoutPassword]: 'Users with no password on file',
  [userNotificationMetrics.usersWithAdminSudoAccess]: 'Users with admin sudo access',
  [userNotificationMetrics.usersWithSambaAccess]: 'Users with samba access',
  [userNotificationMetrics.userWithAdminSudoPasswordlessAccess]: 'Users with admin sudo passwordless access',
  [userNotificationMetrics.usersWithoutDevices]: 'Users without devices',
};

const consoleStatsMetrics = {
  totalUsers: 'totalUsers',
  totalDevices: 'totalDevices',
  totalGroups: 'totalGroups',
  totalApplications: 'totalApplications',
  totalConfigurations: 'totalConfigurations',
};

const userStateMetrics = {
  suspensions: 'suspensions',
  activations: 'activations',
};

const userStateMetricsNames = {
  [userStateMetrics.suspensions]: 'Schedule User Suspension',
  [userStateMetrics.activations]: 'Schedule User Activation',
};

const deviceMetricNames = {
  [deviceMetrics.agentOutOfDateMetric]: 'Device Agent Out Of Date Metric',
  [deviceMetrics.inactiveMetric]: 'Device Inactive Metric',
  [deviceMetrics.uptimeMetric]: 'Device Uptime Metric',
  [deviceMetrics.withoutUsersMetric]: 'Device Without Users Metric',
  [deviceMetrics.withoutPoliciesMetric]: 'Device Without Policies Metric',
};

const consoleStatsNames = {
  [consoleStatsMetrics.totalUsers]: 'Total Users',
  [consoleStatsMetrics.totalGroups]: 'Total User Groups',
  [consoleStatsMetrics.totalDevices]: 'Total Managed Devices',
  [consoleStatsMetrics.totalApplications]: 'Total Managed Software',
  [consoleStatsMetrics.totalConfigurations]: 'Total Device Policies',

};

const homeSupportSectionUrls = {
  resourceLinks: [
    {
      text: 'JumpCloud Community',
      url: 'https://community.jumpcloud.com/',
      name: 'jumpCloudCommunity',
    }, {
      text: 'Support Center',
      url: 'https://support.jumpcloud.com/support/s/',
      name: 'supportCenter',
    }, {
      text: 'Getting Started Guides',
      url: 'https://support.jumpcloud.com/support/s/topic/0TO1M000000EVePWAW/getting-started',
      name: 'gettingStartedGuides',
    }, {
      text: 'Blog',
      url: 'https://jumpcloud.com/blog/',
      name: 'blog',
    },
  ],
  officeHoursLink: {
    text: 'Register Now',
    url: 'https://jumpcloud.com/the-it-hour',
    name: 'itHourRegisterNow',
  },
  jcuLink: {
    text: 'Start Learning Today',
    url: 'https://jumpcloud.com/university',
    name: 'startLearningToday',
  },
};
const homeSettingsText = {
  subheaderText: 'Customize your Dashboard with alerts and widgets reflecting your areas of interest.',
  subtitles: {
    deviceNotifications:
      'Quick view of key Device details.',
    expiredPasswords:
      'Quickly resolve expired passwords by seeing how many Users are going to have trouble logging into their accounts.',
    jcUniversity:
      'Quick access to the JumpCloud education center for IT Admins.',
    learningCenter:
      'Quick access to shortcuts providing admin support.',
    manualGroupMembership:
      'Turn On this widget to see the user Group Membership Updates on your JumpCloud Homepage.',
    mdmCertificateExpirations:
      'See all mdm certificate expirations on your JumpCloud Homepage.',
    newOSReleases:
      'See which OS Releases have come out recently.',
    newUsers:
      'See all new Users that have been created in the past 7 days.',
    officeHours:
      'Quick access to see upcoming community events.',
    reportsReadyForDownload:
      'See which reports are ready for download on your JumpCloud Homepage.',
    scheduledUserActivations:
      'See Users that are scheduled for activation based on a customizable timeframe',
    scheduledUserSuspensions:
      'See Users that are scheduled for suspension based on a customizable timeframe',
    upcomingPasswordExpiration:
      'Based on your password settings, see upcoming password expirations before a User becomes locked out.',
    userGroupSuggestions:
      'See which user group suggestions are available for the users.',
    userLockouts:
      'See which Users have been locked out of their account after too many failed login attempts.',
    userNotifications:
      'Quick view of key User details.',
  },
};

const blogArticles = {
  [onboardingFeatures.ssoAuth]: {
    links: [
      {
        date: 'October 11, 2021',
        title: 'Single Sign-On (SSO) vs Active Directory (AD)',
        url: 'https://jumpcloud.com/blog/sso-vs-ad',
      },
      {
        date: 'October 8, 2021',
        title: 'Why Use JumpCloud Over OneLogin',
        url: 'https://jumpcloud.com/blog/jumpcloud-over-onelogin',
      },
      {
        date: 'September 27, 2021',
        title: 'Open-Source Single Sign-On (SSO)',
        url: 'https://jumpcloud.com/blog/open-source-single-sign-sso',
      },
      {
        date: 'September 22, 2021',
        title: 'How to Choose a Single Sign-On (SSO) Solution',
        url: 'https://jumpcloud.com/blog/sso-business-case',
      },
      {
        date: 'September 23, 2021',
        title: 'How to Test SAML and Configure SSO Using a Free-to-Use Application',
        url: 'https://jumpcloud.com/blog/how-to-test-saml-and-configure-sso-for-free',
      },
    ],
  },
  [onboardingFeatures.ldap]: {
    links: [
      {
        date: 'October 12, 2021',
        title: 'What Is OpenLDAP?',
        url: 'https://jumpcloud.com/blog/what-is-openldap',
      },
      {
        date: 'August 30, 2021',
        title: 'Overview of Cloud LDAP',
        url: 'https://jumpcloud.com/blog/overview-of-cloud-ldap',
      },
      {
        date: 'August 19, 2021',
        title: 'The Difference Between LDAP, OpenLDAP, and Active Directory',
        url: 'https://jumpcloud.com/blog/difference-between-ldap-openldap-active-directory',
      },
      {
        date: 'August 11, 2021',
        title: 'What Is LDAP?',
        url: 'https://jumpcloud.com/blog/what-is-ldap',
      },
      {
        date: 'June 8, 2021',
        title: 'LDAP vs. LDAPS: Securing Auth to Legacy Apps',
        url: 'https://jumpcloud.com/blog/ldap-vs-ldaps',
      },
    ],
  },
  [onboardingFeatures.mfa]: {
    links: [
      {
        date: 'March 17, 2021',
        title: 'Conditional Access Policies to Manage Remote Workers',
        url: 'https://jumpcloud.com/blog/conditional-access-policies-to-manage-remote-workers',
      },
      {
        date: 'February 4, 2021',
        title: 'How Conditional Access Helps Bolster BYOD Policies',
        url: 'https://jumpcloud.com/blog/conditional-access-bolsters-byod-policies',
      },
      {
        date: 'August 24, 2021',
        title: 'One-Touch, Frictionless Multi-Factor Authentication Using JumpCloud Protect™',
        url: 'https://jumpcloud.com/blog/push-notification-mfa-using-jumpcloud-protect',
      },
      {
        date: 'September 14, 2021',
        title: 'The Intersection of Identity and Access Management (IAM) and Multi-Factor Authentication (MFA)',
        url: 'https://jumpcloud.com/blog/the-intersection-of-identity-and-access-management-iam-and-multi-factor-authentication-mfa',
      },
      {
        date: 'July 29, 2021',
        title: 'Evaluating the Accessibility of Different MFA Factors',
        url: 'https://jumpcloud.com/blog/evaluating-the-accessibility-of-different-mfa-factors',
      },
      {
        date: 'June 24, 2021',
        title: 'Simplifying Identity, Access & Device Management in a Hybrid Workplace',
        url: 'https://jumpcloud.com/blog/simplifying-identity-access-device-management-in-a-hybrid-workplace',
      },
    ],
  },
  [onboardingFeatures.radius]: {
    links: [
      {
        date: 'October 7, 2021',
        title: 'How to Maintain Network Security For Remote Workers with RADIUS MFA',
        url: 'https://jumpcloud.com/blog/radius-mfa',
      },
      {
        date: 'July 2, 2021',
        title: 'FreeRADIUS GUI',
        url: 'https://jumpcloud.com/blog/freeradius-gui',
      },
      {
        date: 'June 25, 2021',
        title: 'Free Cloud RADIUS',
        url: 'https://jumpcloud.com/blog/free-cloud-radius',
      },
      {
        date: 'July 27, 2021',
        title: 'Federated Identity Manager',
        url: 'https://jumpcloud.com/blog/federated-identity-manager',
      },
      {
        date: 'June 16, 2021',
        title: 'LDAP vs RADIUS',
        url: 'https://jumpcloud.com/blog/ldap-vs-radius',
      },
    ],
  },
  [onboardingFeatures.compliance]: {
    links: [
      {
        date: 'September 15, 2021',
        title: 'Managing Device Health & Telemetry',
        url: 'https://jumpcloud.com/blog/managing-device-health-telemetry',
      },
      {
        date: 'October 1, 2021',
        title: 'Improper Offboarding Poses Significant Security Risks',
        url: 'https://jumpcloud.com/blog/improper-offboarding-poses-significant-security-risks',
      },
      {
        date: 'July 21, 2021',
        title: 'Staying SOC 2 Compliant with Remote Workers',
        url: 'https://jumpcloud.com/blog/staying-soc-2-compliant-remote-workers',
      },
      {
        date: 'October 16, 2020',
        title: 'Transform Logs into Knowledge with Directory Insights',
        url: 'https://jumpcloud.com/blog/directory-insights-schema',
      },
      {
        date: 'August 25, 2020',
        title: 'Event Logging Across Your Directory with Directory Insights',
        url: 'https://jumpcloud.com/blog/directory-insights-directory-event-logging-data',
      },
    ],
  },
  [onboardingFeatures.userManagement]: {
    links: [
      {
        date: 'March 23, 2021',
        title: 'User Provisioning & Active Directory',
        url: 'https://jumpcloud.com/blog/user-provisioning-ad',
      },
      {
        date: 'February 16, 2021',
        title: 'How to Simplify Onboarding Employees to Their Applications with SCIM',
        url: 'https://jumpcloud.com/blog/how-to-simplify-onboarding-employees-to-their-applications-with-scim',
      },
      {
        date: 'July 12, 2021',
        title: 'How ClassPass Adapted to the Pandemic & Remote Work with a Cloud Directory',
        url: 'https://jumpcloud.com/blog/how-classpass-adapted-to-the-pandemic-remote-work-with-a-cloud-directory',
      },
      {
        date: 'July 7, 2021',
        title: 'Integrating Data Security Into the IT Onboarding Journey',
        url: 'https://jumpcloud.com/blog/integrating-data-security-it-onboarding-journey',
      },
      {
        date: 'April 23, 2021',
        title: 'Creating Users Through Automation – Integromat Integration Tutorial',
        url: 'https://jumpcloud.com/blog/creating-users-through-automation-integromat-integration-tutorial',
      },
    ],
  },
  [onboardingFeatures.deviceManagement]: {
    links: [
      {
        date: 'September 28, 2021',
        title: 'How to Defer Windows 11 Updates',
        url: 'https://jumpcloud.com/blog/how-to-defer-windows-11-updates',
      },
      {
        date: 'September 20, 2021',
        title: 'Remote Software & OS Update Management as Part of Modern Device Management',
        url: 'https://jumpcloud.com/blog/remote-software-and-os-update-management',
      },
      {
        date: 'September 15, 2021',
        title: 'Managing Device Health & Telemetry',
        url: 'https://jumpcloud.com/blog/managing-device-health-telemetry',
      },
      {
        date: 'September 10, 2021',
        title: 'How to Develop a Patch Management Process',
        url: 'https://jumpcloud.com/blog/how-to-develop-a-patch-management-process',
      },
      {
        date: 'July 14, 2021',
        title: 'Zero Touch Enrollment & Deployment',
        url: 'https://jumpcloud.com/blog/zero-touch-enrollment-device-deployment',
      },
    ],
  },
  [onboardingFeatures.serverManagement]: {
    links: [
      {
        date: 'December 15, 2020',
        title: 'SSH Key Life Cycle Automation and Management',
        url: 'https://jumpcloud.com/blog/ssh-key-life-cycle-automation',
      },
      {
        date: 'May 9, 2020',
        title: 'Managing Linux After SCCM Support EOL',
        url: 'https://jumpcloud.com/blog/sccm-linux-support-eol',
      },
      {
        date: 'May 4, 2020',
        title: 'Why Jump Servers Are Obsolete',
        url: 'https://jumpcloud.com/blog/jump-servers-obsolete',
      },
      {
        date: 'February 26, 2020',
        title: 'RDP vs. SSH',
        url: 'https://jumpcloud.com/blog/rdp-ssh',
      },
      {
        date: 'February 21, 2020',
        title: 'Top 9 Windows Server Roles and their Alternatives',
        url: 'https://jumpcloud.com/blog/top-windows-server-roles-alternatives',
      },
    ],
  },
};

const resourcesTabs = {
  videos: 'videos',
  simulations: 'simulations',
};

const resourcesTabNames = {
  [resourcesTabs.videos]: 'Videos',
  [resourcesTabs.simulations]: 'Guided Simulations',
};

const resources = {
  [onboardingFeatures.ssoAuth]: {
    [resourcesTabs.videos]: [
      {
        label: 'SAML Setup',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/ro0cqt7gbt',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=ro0cqt7gbt',
      },
      {
        label: 'SSO with MFA',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/1lodgwbut6',
        url: 'https://jumpcloud-1.wistia.com/medias/1lodgwbut6',
      },
      {
        label: 'SCIM-SSO for Zoom',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/rko6ixdrxf',
        url: 'https://jumpcloud-1.wistia.com/medias/rko6ixdrxf',
      },
    ],
  },
  [onboardingFeatures.ldap]: {
    [resourcesTabs.videos]: [
      {
        label: 'Configuring LDAP',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/w7vgst9odt',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=w7vgst9odt',
      },
    ],
  },
  [onboardingFeatures.appleMdm]: {
    [resourcesTabs.videos]: [
      {
        label: 'Setting up MDM',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/kx7a6y116f',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=kx7a6y116f',
      },
    ],
    [resourcesTabs.simulations]: [
      {
        label: 'MDM Configuration',
        iframeUrl: 'https://xd.adobe.com/embed/3f06e8ed-b623-4355-aae1-135be1c1501d-6a55/',
        url: 'https://jumpcloud.com/simulations/mdm-configuration',
      },
    ],
  },
  [onboardingFeatures.compliance]: {
    [resourcesTabs.videos]: [
      {
        label: 'Intro to Directory Insights',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/1t4v7uqdjx',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=1t4v7uqdjx',
      },
    ],
  },
  [onboardingFeatures.mfa]: {
    [resourcesTabs.videos]: [
      {
        label: 'TOTP MFA for Users and Admins',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/deczhe338e',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=deczhe338e',
      },
      {
        label: 'Using Push MFA',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/hujq98p3ic',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=hujq98p3ic',
      },
      {
        label: 'Enrolling in Push MFA',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/ex50s49jeq',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=ex50s49jeq',
      },
      {
        label: 'Device Trust Policy',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/o1w8zw88ad',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=o1w8zw88ad',
      },
      {
        label: 'MFA for Devices',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/ykua040lwc',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=ykua040lwc',
      },
      {
        label: 'WebAuthn MFA',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/y7w5r9xjf2',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=y7w5r9xjf2',
      },
    ],
    [resourcesTabs.simulations]: [
      {
        label: 'Conditional Access: SSO Applications',
        iframeUrl: 'https://xd.adobe.com/embed/95a67e8d-5e5c-4233-ae63-bf56b632734a-c0f5/',
        url: 'https://jumpcloud.com/simulations/conditional-access-sso-applications',
      },
      {
        label: 'Conditional Access: Identity Trust',
        iframeUrl: 'https://xd.adobe.com/embed/d6c77677-273d-4ad6-85af-dac3b14dbfed-8394/',
        url: 'https://jumpcloud.com/simulations/conditional-access-identity-trust',
      },
      {
        label: 'Conditional Access: Device Trust',
        iframeUrl: 'https://xd.adobe.com/embed/d9a5d73a-67ee-4d25-8956-b3aa1aa4b853-8cf6/',
        url: 'https://jumpcloud.com/simulations/conditional-access-device-trust',
      },
      {
        label: 'Condtional Access: Network Trust',
        iframeUrl: 'https://xd.adobe.com/embed/50c9ca47-22da-40fb-a717-a4005738beca-059a/',
        url: 'https://jumpcloud.com/simulations/conditional-access-network-trust',
      },
      {
        label: 'Conditional Access: Geolocation',
        iframeUrl: 'https://xd.adobe.com/embed/0f53e373-6b68-4fad-9358-a649dc8df477-5a00/',
        url: 'https://jumpcloud.com/simulations/conditional-access-geolocation',
      },
    ],
  },
  [onboardingFeatures.radius]: {
    [resourcesTabs.videos]: [
      {
        label: 'Configuring RADIUS',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/asv2crna94',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=asv2crna94',
      },
      {
        label: 'What is Cloud RADIUS?',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/ihfsjqto1v',
        url: 'https://jumpcloud-1.wistia.com/medias/ihfsjqto1v',
      },
      {
        label: 'RADIUS Quick Demo',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/vx5ahvezr9',
        url: 'https://jumpcloud-1.wistia.com/medias/vx5ahvezr9',
      },
    ],
  },
  [onboardingFeatures.userManagement]: {
    [resourcesTabs.videos]: [
      {
        label: 'SCIM for Salesforce',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/tjabxzfiya',
        url: 'https://jumpcloud-1.wistia.com/medias/tjabxzfiya',
      },
      {
        label: 'SCIM for Slack',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/s7592mvq4y',
        url: 'https://jumpcloud-1.wistia.com/medias/s7592mvq4y',
      },
      {
        label: 'SCIM-SSO for Zoom',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/rko6ixdrxf',
        url: 'https://jumpcloud-1.wistia.com/medias/rko6ixdrxf',
      },
      {
        label: 'Just-in-Time Provisioning',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/qhxfpyfw3j',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=qhxfpyfw3j',
      },
    ],
    [resourcesTabs.simulations]: simulations[ItemTypeConsts.user],
  },
  [onboardingFeatures.deviceManagement]: {
    [resourcesTabs.videos]: [
      {
        label: 'Adding Windows Devices',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/l9i6buim2f',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=l9i6buim2f',
      },
      {
        label: 'Adding Mac Devices',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/bp5b3ykoo9',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=bp5b3ykoo9',
      },
      {
        label: 'Adding Linux Devices',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/a59xx8pnma',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=a59xx8pnma',
      },
      {
        label: 'Configuring Policies',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/4was06wfnf',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=4was06wfnf',
      },
      {
        label: 'MFA for Devices',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/ykua040lwc',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=ykua040lwc',
      },
      {
        label: 'Remote Agent Install',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/fwe2jquluk',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=fwe2jquluk',
      },
      {
        label: 'Device Groups',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/jplvc0x3km',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=jplvc0x3km',
      },
      {
        label: 'Blocking Windows 11 Installation',
        iframeUrl: 'https://jumpcloud-1.wistia.com/embed/iframe/i39sf3dbh6',
        url: 'https://jumpcloud-1.wistia.com/medias/i39sf3dbh6',
      },
    ],
    [resourcesTabs.simulations]: simulations[ItemTypeConsts.device],
  },
  [onboardingFeatures.serverManagement]: {
    [resourcesTabs.videos]: [
      {
        label: 'Adding Linux Devices',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/a59xx8pnma',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=a59xx8pnma',
      },
      {
        label: 'Adding Windows Devices',
        iframeUrl: 'https://fast.wistia.net/embed/iframe/l9i6buim2f',
        url: 'https://fast.wistia.net/embed/channel/6mrjdjaaty?wchannelid=6mrjdjaaty&wmediaid=l9i6buim2f',
      },
    ],
  },
};

Object.keys(blogArticles).forEach(key => {
  const updatedLinks = blogArticles[key].links.map(link => {
    const newLink = { ...link, feature: key };
    return newLink;
  });

  blogArticles[key].links = updatedLinks;
});

export default {
  riskTypes: {
    low: 'low',
    high: 'high',
  },

  blogArticles,
  consoleStatsMetrics,
  consoleStatsNames,
  deviceMetrics,
  deviceMetricNames,
  homeSupportSectionUrls,
  homeSettingsText,
  apiPayloadConst,
  recentOsReleasesNames,
  resources,
  resourcesTabs,
  resourcesTabNames,
  userNotificationMetrics,
  userNotificationMetricsNames,
  userStateMetrics,
  userStateMetricsNames,
  widgetActions,
  widgetDisplayName,
  widgetIcons,
  widgetTypes,
  widgetNames,
  widgetItemTypes,
  widgetTableColumns,
  widgetSortOption,
};
